/**
 * Single source of truth for cart/checkout price and tax math.
 *
 * Before this, Cart.jsx, CheckOut.jsx and CartItem.jsx each had their own
 * copy-pasted version of this logic — one discounted the net (HT) price and
 * derived TTC by adding tax back on, another discounted the gross (TTC)
 * price directly, and each rounded to 2 decimals at a different
 * intermediate step before multiplying by quantity and summing across
 * items. Those are algebraically equivalent in exact arithmetic, but
 * rounding at different points produces genuine cent-level drift once
 * summed across a multi-item cart — which is why QA saw the Cart page and
 * the Checkout page show different "wrong" HT/VAT/TTC totals for the same
 * cart. Keep everything in full floating-point precision until the very
 * last step, and round only once, for display.
 */

// An item with less than 1 unit in stock is excluded from totals — mirrors
// the existing business rule in Cart.jsx/CheckOut.jsx.
export function isPurchasable(item) {
  return Number(item?._qte_a_terme_calcule) >= 1;
}

/**
 * Per-unit net (HT), tax (VAT) and gross (TTC) amounts for a single cart
 * line, in EUR (the currency articles are actually priced in), with any
 * percentage discount applied. Not multiplied by quantity.
 */
export function lineUnitAmounts(item) {
  const priceHT = Number(item?.price) || 0;
  const priceTTC = Number(item?.price_ttc) || 0;
  const discount = Number(item?.discount) || 0;
  const factor = discount > 0 ? 1 - discount / 100 : 1;

  const ht = priceHT * factor;
  const ttc = priceTTC * factor;
  const tva = ttc - ht;

  return { ht, tva, ttc };
}

/**
 * Sums HT/VAT/TTC/weight across every purchasable line in `items`
 * (respecting quantity), in EUR, in full precision — round only when
 * displaying or persisting the result via money()/round2().
 */
export function cartTotals(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (totals, item) => {
      if (!isPurchasable(item)) return totals;
      const qty = Number(item?.quantity) || 0;
      const { ht, tva, ttc } = lineUnitAmounts(item);
      totals.ht += ht * qty;
      totals.tva += tva * qty;
      totals.ttc += ttc * qty;
      totals.weight += qty * (Number(item?.weight) || 0);
      return totals;
    },
    { ht: 0, tva: 0, ttc: 0, weight: 0 }
  );
}

// Round to 2 decimals for display/persistence — the one place this should
// happen, after every sum/multiply already ran at full precision.
export function round2(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

// Convert an EUR amount to the requested display currency and round once.
// Returns a NUMBER — for arithmetic and for payload fields sent to the
// backend (order_invoice_items etc.), where the original code always sent
// a number (parseFloat(...)), never a string.
export function money(amount, currency, rate = 1) {
  const value = currency === "usd" ? (Number(amount) || 0) * (Number(rate) || 1) : Number(amount) || 0;
  return round2(value);
}

// Same conversion as money(), but for on-screen display: always returns a
// string fixed to 2 decimals ("3.00", not "3") — money()'s numeric 3 would
// otherwise render as "3" and silently drop the cents place.
export function formatMoney(amount, currency, rate = 1) {
  return money(amount, currency, rate).toFixed(2);
}
